"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {useRouter} from "@bprogress/next";
import {useTranslation} from "react-i18next";
import {createAgencyConnection} from "@/actions-client/settings/actions";
import {addIntegration} from "@/actions/integrations/actions";
import {
   initiateGoogleNodeAuthorization,
   isNodeProviderConnected,
} from "@/actions/workflows/actions";
import {connectMomentumCredential} from "@/actions/workflows/momentum";
import {type FormModalData, type FormModalSection} from "@/components/common/FormModal";
import {Partners} from "@/enums/agencies";
import {useAlert} from "@/hooks/useAlert";
import {UnifiedIntegration} from "@/types/integrations";
import {handledSessionExpiry, unwrapSessionExpiry} from "@/utils/common/sessionExpiry";
import {isMomentumIntegrationKey, normalizeAlphaNum} from "@/utils/integrations/marketplace-config";
import {
   navigateToAuthorizationUrl,
   openOAuthPopup,
   waitForPopupClose,
} from "@/utils/integrations/oauthPopup";
import {convertSchemaToFormModalData} from "@/utils/common/schemaValidator";
import {convertFormDataToApiFormat} from "@/utils/tools/integrations";
import {
   extractCredentialsSchema,
   extractSettingsSchema,
   FALLBACK_SCHEMAS,
   FALLBACK_SETTINGS_SCHEMAS,
   findIntegrationWithSchema,
   getFallbackSchemaKey,
   type IntegrationWithSchema,
} from "./schema-utils";

/** Canonical names in Galactus for connectable integrations: AgencyZoom (agency modal), Momentum, AMS360, HawkSoft (schema modal). */
const INTEGRATION_TO_PARTNER: Record<string, Partners> = {
   agencyzoom: Partners.AgencyZoom,
};
const AGENCY_CONNECT_IDS = new Set(["agencyzoom"]);
const INTERNAL_GOOGLE_DRIVE_KEYS = new Set(["googledrive"]);
const GOOGLE_OAUTH_POPUP_NAME = "gail-google-drive-oauth";

interface UseIntegrationConnectionParams {
   availableIntegrations: IntegrationWithSchema[];
   onConnected: (integrationId: string, raccoonId?: string) => void;
   vt: (val: string) => string;
}

export function useIntegrationConnection({
   availableIntegrations,
   onConnected,
   vt,
}: UseIntegrationConnectionParams) {
   const router = useRouter();
   const alertContext = useAlert();
   const {t} = useTranslation("partners");

   // Schema-based connection modal
   const [connectionModalOpen, setConnectionModalOpen] = useState(false);
   const [connectionTarget, setConnectionTarget] = useState<UnifiedIntegration | null>(null);
   const [connectionModalSections, setConnectionModalSections] = useState<FormModalSection[]>([]);

   // Agency connection modal
   const [agencyModalOpen, setAgencyModalOpen] = useState(false);
   const [agencyTarget, setAgencyTarget] = useState<UnifiedIntegration | null>(null);
   const [agencyUsername, setAgencyUsername] = useState("");
   const [agencyPassword, setAgencyPassword] = useState("");
   const [isConnectingAgency, setIsConnectingAgency] = useState(false);

   const isGoogleDriveIntegration = useCallback((integration: UnifiedIntegration): boolean => {
      const normalizedKey = normalizeAlphaNum(integration.key ?? "");
      const normalizedName = normalizeAlphaNum(integration.name);
      return (
         INTERNAL_GOOGLE_DRIVE_KEYS.has(normalizedKey) ||
         INTERNAL_GOOGLE_DRIVE_KEYS.has(normalizedName)
      );
   }, []);

   // The watch on the current popup, aborted on unmount so its poll cannot
   // outlive the marketplace.
   const popupWatchRef = useRef<AbortController | null>(null);
   useEffect(() => () => popupWatchRef.current?.abort(), []);

   const connectGoogleDriveIntegration = useCallback(
      async (integration: UnifiedIntegration) => {
         // Opened before the await, or Safari treats the window as unsolicited.
         const popup = openOAuthPopup(GOOGLE_OAUTH_POPUP_NAME);
         // Bounds the wait and stops the poll when the popup is abandoned. It
         // used to run for the life of the tab.
         const watch = new AbortController();

         try {
            const authorizationResult = await initiateGoogleNodeAuthorization();
            if (handledSessionExpiry(authorizationResult)) {
               popup?.close();
               return;
            }
            const authorizationUrl = unwrapSessionExpiry(authorizationResult, null);
            if (!authorizationUrl) {
               throw new Error("Google authorization URL not returned");
            }

            // Refuses anything but https, into the catch below: the popup is
            // same-origin with us until it navigates, so an unvetted URL would
            // execute in our origin with a live opener.
            if (navigateToAuthorizationUrl(popup, authorizationUrl) === "redirect") {
               return;
            }
            if (!popup) return;

            // Omega's callback postMessage targets the workflows API origin, so
            // the opener (gail-ui) never receives it. Poll popup close, then
            // confirm the credential was stored.
            popupWatchRef.current?.abort();
            popupWatchRef.current = watch;
            await waitForPopupClose(popup, watch.signal);

            // Aborted means the marketplace unmounted or a newer connect
            // superseded this one, so this attempt has no one left to report to.
            if (watch.signal.aborted) return;

            const connected = unwrapSessionExpiry(
               await isNodeProviderConnected("google"),
               false
            );
            if (!connected) return;

            onConnected(integration.id);
            alertContext?.setAlert?.({
               status: "success",
               message: t("marketplaceConnectedSuccess", {name: integration.name}),
               secondsToClose: 10,
            });
         } catch (error) {
            watch.abort();
            popup?.close();
            console.error("Error starting Google Drive OAuth:", error);
            alertContext?.setAlert?.({
               status: "error",
               message: t("marketplaceConnectionFailed"),
               secondsToClose: 10,
            });
         }
      },
      [alertContext, onConnected, t]
   );

   const handleConnect = useCallback(
      (i: UnifiedIntegration) => {
         const key = i.key || "";
         const fallbackKey = getFallbackSchemaKey(key);

         if (AGENCY_CONNECT_IDS.has(fallbackKey)) {
            setAgencyTarget(i);
            setAgencyModalOpen(true);
            return;
         }

         if (isGoogleDriveIntegration(i) && !i.route) {
            void connectGoogleDriveIntegration(i);
            return;
         }

         if (i.route) {
            router.push(i.route);
            return;
         }

         const integrationWithSchema = findIntegrationWithSchema(availableIntegrations, i);
         const schema =
            extractCredentialsSchema(integrationWithSchema) ||
            FALLBACK_SCHEMAS[fallbackKey] ||
            null;

         const hasAnySchema =
            schema !== null || FALLBACK_SETTINGS_SCHEMAS[fallbackKey] !== undefined;

         if (hasAnySchema) {
            try {
               // Try credentials schema first; if it yields no visible fields
               // (e.g. all const), fall back to settings schema for form generation.
               let fields: FormModalData[] = [];
               if (schema) {
                  fields = convertSchemaToFormModalData(schema, vt);
               }
               if (!fields.length && FALLBACK_SETTINGS_SCHEMAS[fallbackKey]) {
                  fields = convertSchemaToFormModalData(FALLBACK_SETTINGS_SCHEMAS[fallbackKey], vt);
               }
               if (fields && Array.isArray(fields) && fields.length > 0) {
                  setConnectionModalSections([
                     {
                        title: t("marketplaceAuthentication"),
                        description: t("marketplaceEnterCredentials"),
                        fields,
                     },
                  ]);
                  setConnectionTarget(i);
                  setTimeout(() => setConnectionModalOpen(true), 0);
                  return;
               }
            } catch (error) {
               console.error("Error converting schema for", i.name, error);
            }
         }

         alertContext?.setAlert?.({
            status: "error",
            message: t("marketplaceSchemaMissing", {name: i.name}),
            secondsToClose: 10,
         });
      },
      [
         availableIntegrations,
         vt,
         alertContext,
         t,
         router,
         isGoogleDriveIntegration,
         connectGoogleDriveIntegration,
      ]
   );

   const handleConnectionSave = useCallback(
      async (values: FormModalData[] | null) => {
         if (!values || !connectionTarget) {
            setConnectionModalOpen(false);
            return;
         }

         try {
            const key = connectionTarget.key || "";
            const fallbackKey = getFallbackSchemaKey(key);

            const integrationWithSchema = findIntegrationWithSchema(
               availableIntegrations,
               connectionTarget
            );

            let credentialsSchema: any = extractCredentialsSchema(integrationWithSchema);
            let settingsSchema: any = undefined;
            let integrationName: string = key;
            let raccoonId: string | undefined;

            if (credentialsSchema) {
               settingsSchema = extractSettingsSchema(integrationWithSchema);
               integrationName = integrationWithSchema?.name || integrationName;
            }

            if (!credentialsSchema && FALLBACK_SCHEMAS[fallbackKey]) {
               credentialsSchema = FALLBACK_SCHEMAS[fallbackKey];
               integrationName = fallbackKey;
            }

            if (!settingsSchema && FALLBACK_SETTINGS_SCHEMAS[fallbackKey]) {
               settingsSchema = FALLBACK_SETTINGS_SCHEMAS[fallbackKey];
            }

            if (!credentialsSchema && !settingsSchema) {
               throw new Error(`Integration schema not found for ${connectionTarget.name}.`);
            }

            const {serverCredentials, settings} = convertFormDataToApiFormat(
               values,
               credentialsSchema ?? {},
               settingsSchema
            );

            const isAgency = fallbackKey === "agencyzoom";

            if (isAgency) {
               const partner = INTEGRATION_TO_PARTNER[fallbackKey];
               if (!partner) throw new Error("Partner mapping not found");
               const username = serverCredentials.username || serverCredentials.clientId;
               const password = serverCredentials.password || serverCredentials.clientSecret;
               if (!username || !password) throw new Error("Credentials required");
               const resp = await createAgencyConnection({
                  agency: partner,
                  username: String(username),
                  password: String(password),
               });
               if (!resp.success) throw new Error("Connection failed");
            } else {
               // Temporary double write: the workflow nodes read Omega's row, the
               // tile still reads Raccoon's. Omega goes first because it checks
               // the login against Momentum, so a rejected one never reaches
               // Raccoon. There is no rollback if Raccoon then fails: the tile
               // reads not-connected, and a retry replaces the Omega row.
               if (isMomentumIntegrationKey(key)) {
                  const username = serverCredentials.clientId ?? serverCredentials.username;
                  const password = serverCredentials.clientSecret ?? serverCredentials.password;
                  const momentum = await connectMomentumCredential(
                     String(username ?? ""),
                     String(password ?? "")
                  );
                  if (handledSessionExpiry(momentum)) return;
                  if (!momentum.ok && momentum.reason === "verification_failed") {
                     // The modal stays open so the customer can correct the login.
                     alertContext?.setAlert?.({
                        status: "error",
                        message: t("marketplaceMomentumVerificationFailed"),
                        secondsToClose: 10,
                     });
                     return;
                  }
                  if (!momentum.ok) throw new Error("Momentum credential was not stored");
               }

               const result = await addIntegration({
                  name: integrationName,
                  credentials: serverCredentials,
                  settings,
               });
               // TODO: tighten check when addIntegration return type is clarified
               if (!result) throw new Error("Connection failed");
               raccoonId = result?.data?.id ?? result?.id;
            }

            onConnected(connectionTarget.id, raccoonId);

            alertContext?.setAlert?.({
               status: "success",
               message: t("marketplaceConnectedSuccess", {name: connectionTarget.name}),
               secondsToClose: 10,
            });

            setConnectionModalOpen(false);
            setConnectionTarget(null);
         } catch (error) {
            console.error("Error connecting integration:", error);
            alertContext?.setAlert?.({
               status: "error",
               message: t("marketplaceConnectionFailed"),
               secondsToClose: 10,
            });
         }
      },
      [connectionTarget, availableIntegrations, alertContext, onConnected, t]
   );

   const handleConnectionCancel = useCallback(() => {
      setConnectionModalOpen(false);
      setConnectionTarget(null);
   }, []);

   const handleAgencyConnectionSave = useCallback(async () => {
      if (!agencyTarget || !agencyUsername || !agencyPassword) return;

      const fallbackKey = getFallbackSchemaKey(agencyTarget.key || "");
      const partner = INTEGRATION_TO_PARTNER[fallbackKey];
      if (!partner) {
         alertContext?.setAlert?.({
            status: "error",
            message: t("marketplaceNotSupported"),
            secondsToClose: 4,
         });
         return;
      }

      setIsConnectingAgency(true);
      try {
         const response = await createAgencyConnection({
            agency: partner,
            username: agencyUsername,
            password: agencyPassword,
         });
         if (!response.success) throw new Error("Connection failed");

         onConnected(agencyTarget.id);

         alertContext?.setAlert?.({
            status: "success",
            message: t("marketplaceConnectedSuccess", {name: agencyTarget.name}),
            secondsToClose: 10,
         });

         setAgencyModalOpen(false);
         setAgencyTarget(null);
         setAgencyUsername("");
         setAgencyPassword("");
      } catch (error) {
         console.error("Error connecting agency:", error);
         alertContext?.setAlert?.({
            status: "error",
            message: t("marketplaceConnectionFailed"),
            secondsToClose: 10,
         });
      } finally {
         setIsConnectingAgency(false);
      }
   }, [agencyTarget, agencyUsername, agencyPassword, alertContext, onConnected, t]);

   const handleAgencyConnectionCancel = useCallback(() => {
      setAgencyModalOpen(false);
      setAgencyTarget(null);
      setAgencyUsername("");
      setAgencyPassword("");
   }, []);

   return {
      handleConnect,
      // Schema connection modal
      connectionModalOpen,
      connectionTarget,
      connectionModalSections,
      handleConnectionSave,
      handleConnectionCancel,
      // Agency connection modal
      agencyModalOpen,
      agencyTarget,
      agencyUsername,
      setAgencyUsername,
      agencyPassword,
      setAgencyPassword,
      isConnectingAgency,
      handleAgencyConnectionSave,
      handleAgencyConnectionCancel,
   };
}
